import { MediaTextMosaicDS } from '@src/components/blocks/media-text-mosaic-ds'
import type { MediaTextMosaicRow } from '@src/components/blocks/media-text-mosaic-ds'
import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import {
  getFeatureSectionTwoAnalytics,
  useFeatureSectionTwoImpressionRef,
} from '@src/components/widgets/use-ia-impression'

import { resolveSectionBackground } from '../widget-background'
import { toRichText } from '../widget-utils'

import type { MediaTextMosaicWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Matches legacy's root testid verbatim so QA can select the widget root the same way in either mode.
// @i18n-ignore
const MEDIA_TEXT_MOSAIC_WIDGET_TEST_ID = 'media-text-mosaic-widget'

/**
 * DS branch of the PL `MEDIA_TEXT_MOSAIC` widget: normalizes each proto item into a
 * typed `MediaTextMosaicRow` and delegates rendering to `MediaTextMosaicDS`.
 * `classNames` are cast until the proto carries them (legacy parity — bg is transparent today).
 */
export function MediaTextMosaicDSWidget(props: PLTypedWidgetProps<MediaTextMosaicWidgetData>) {
  const data = props.data.mediaTextMosaicData
  const items = data?.items ?? []
  // MediaTextMosaic sections are FS2 by definition, so no isFS2NewDesignShape gate (empty sections skip the wrapper below).
  const impressionRef = useFeatureSectionTwoImpressionRef(getFeatureSectionTwoAnalytics(data))

  const rows: MediaTextMosaicRow[] = items.map((item, index) => {
    const classNames = (item as { classNames?: string[] } | undefined)?.classNames
    const document = toRichText(item.subTitle)?.raw
    return {
      key: `${item.title}-${index}`,
      media: item.media,
      position: item.position,
      title: item.title,
      subTitle: document?.content.length ? document : undefined,
      backgroundColor: resolveSectionBackground(classNames),
    }
  })

  const content = <MediaTextMosaicDS rows={rows} testId={MEDIA_TEXT_MOSAIC_WIDGET_TEST_ID} />
  // Empty section: the block renders null; return it bare so we emit no wrapper and fire no impression
  // for a section the user never sees (mirrors feature-showcase / product-comparison-carousel).
  if (rows.length === 0) return content
  return <div ref={impressionRef}>{content}</div>
}
